import { storage } from '@uhg-abyss/web/tools/storage';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { Text } from '@uhg-abyss/web/ui/Text';
import includes from 'lodash/includes';
import toUpper from 'lodash/toUpper';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { Constants } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { LOADER_CENTER_STYLES } from '../../../../common/ConstantsStyles';
import { MemberInfo } from '../../../../common/PSXHeader/ObapiDemo/memberProfile';
import {
  getGeoLocationFromStorage,
  setGeoLocationToStorage,
} from '../../../../common/PSXHeader/SearchBar/utils';
import { getFeatureFlag } from '../../../../common/Utils';
import { findLoggedInMember } from '../../../../common/Utils/memberUtils/memberUtils';
import { CountySearchContext } from '../../../../context/CountySearchContext';
import { PortalContext } from '../../../../context/PortalContext';
import { useDeviceLocation } from '../../../../hooks/useDeviceLocation';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useLocation } from '../../../../hooks/useLocation';
import { useLocationCoordinate } from '../../../../hooks/useLocationCoordinate';
import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import { CustomLocation, Location } from '../../../../models/Location';
import { useGuestStore } from '../../../../store/useGuestStore';
import { GuestState } from '../../../../store/useGuestStore/guestStore';
import {
  getMemberLocation,
  hasIncompleteAddress,
} from '../../../../utils/psxHeader.utils';
import { isNonMember } from '../../../../utils/user.utils';
import { LocationInput } from './LocationInput';

interface Props {
  children?: React.ReactNode;
  handleOnClose?: () => void;
  initialLocationStatus?: string;
  isOpenLocationChange?: Boolean;
}

export const SearchLocation = ({
  children,
  handleOnClose,
  initialLocationStatus = '',
  isOpenLocationChange = false,
}: Props) => {
  const { t } = useTranslation();
  const { PAGE_REFRESH } = Constants.STORAGE_KEYS.SESSION;
  const pageRefresh = storage.session.get(PAGE_REFRESH) || false;

  const big5 = useStore(StoreKeys.PLAN_SELECTOR);
  const obapimembers = useStore(StoreKeys.OBAPI_MEMBERS);
  const [memberProfiles] = useState<MemberInfo[]>(obapimembers);
  const featureFlags = useLagoon('feature-flags')();
  const { setIsCountySearch } = useContext(CountySearchContext);
  const { portalData } = useContext(PortalContext);
  const [isDevLocLoading, setIsDevLocLoading] = useState(false);

  const { guestData } = useGuestStore(
    useShallow((state: GuestState) => ({
      guestData: state.guestData,
    }))
  );

  const enableSearchLocation = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SEARCH_LOCATION
  );

  const enableMemberDefaultLocation = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MEMBER_DEFAULT_LOCATION
  );

  const isAWSLocationSearchEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.SWITCH_TO_AWS_LOCATION_SEARCH
  );

  const heading = `${Constants.SET_LOCATION.LOCATION}`;

  const loggedInMemberIndex = findLoggedInMember(memberProfiles, big5.bigN);
  const memberLocation = getMemberLocation(
    memberProfiles[loggedInMemberIndex],
    enableMemberDefaultLocation
  );
  const config = useLagoon('config')();
  const splitedMemberLocation = memberLocation.split(' ');
  const filterdMemberLocation = splitedMemberLocation.filter(
    (str) => str !== 'null'
  );

  const countryCodes =
    config?.find(
      (item) => item.key === 'ADDITIONAL_COUNTRIES_FOR_LOCATION_SEARCH'
    )?.value ?? '';

  const locationStates = {
    HAS_LOCATION: 'HAS_LOCATION',
    LOADING: 'LOADING',
    LOCATION_NOT_FOUND: 'LOCATION_NOT_FOUND',
  };
  const callingUser = portalData?.portalEligibilityData?.callingUser;
  const shouldUseLocationFromStorage = pageRefresh || !isNonMember(callingUser);

  const geoLocationName =
    getGeoLocationFromStorage()?.name &&
    (isAWSLocationSearchEnabled ||
      getGeoLocationFromStorage()?.stateCode?.trim());
  shouldUseLocationFromStorage ? getGeoLocationFromStorage()?.name : '';
  const [currentLocationStatus, setCurrentLocationStatus] = useState<string>(
    geoLocationName ? locationStates.HAS_LOCATION : initialLocationStatus
  );
  const [customLocation, setCustomLocation] = useState<CustomLocation>();
  storage.session.set(PAGE_REFRESH, true);

  const [, getLocationCoordinate] = useLocationCoordinate({
    onCompleted: (result) => {
      const targetLocation: Location =
        result?.data?.locationCoordinate?.features?.[0] || {};

      if (!targetLocation?.id) {
        setCurrentLocationStatus(locationStates.LOCATION_NOT_FOUND);
        return;
      }

      const {
        id,
        center = [],
        place_name: placeName = '',
        stateCode = '',
      } = targetLocation;
      const [long, lat] = center;

      if (includes(toUpper(id), toUpper(Constants.DISTRICT)))
        setIsCountySearch(true);
      else setIsCountySearch(false);

      const location = {
        id,
        name: placeName,
        longitude: long,
        latitude: lat,
        stateCode,
      };

      setGeoLocationToStorage(location);
      setCurrentLocationStatus(locationStates.HAS_LOCATION);
    },
    onError: () => {},
  });

  const [, getLocation] = useLocation({
    onCompleted: async (result) => {
      const targetLocation: Location =
        result?.data?.location?.features?.[0] || {};

      if (!targetLocation?.id) {
        setCurrentLocationStatus(locationStates.LOCATION_NOT_FOUND);
        return;
      }

      // Fetch coordinates using GetLocationCoordinate for accurate lat/long if AWS feature is enabled
      if (isAWSLocationSearchEnabled) {
        await getLocationCoordinate({
          variables: {
            placeID: targetLocation.id,
          },
        });
      } else {
        // Use the coordinates from GetLocation response
        const {
          id,
          center = [],
          place_name: placeName = '',
          stateCode = '',
        } = targetLocation;
        const [long, lat] = center;

        if (includes(toUpper(id), toUpper(Constants.DISTRICT)))
          setIsCountySearch(true);
        else setIsCountySearch(false);

        const location = {
          id,
          name: placeName,
          longitude: long,
          latitude: lat,
          stateCode,
        };

        setGeoLocationToStorage(location);
        setCurrentLocationStatus(locationStates.HAS_LOCATION);
      }
    },
  });
  const { getDeviceLocation } = useDeviceLocation(
    () => {},
    setIsDevLocLoading,
    setCurrentLocationStatus,
    enableMemberDefaultLocation
  );

  useEffect(() => {
    if (isOpenLocationChange) {
      setCurrentLocationStatus(locationStates.LOCATION_NOT_FOUND);
    }
  }, [isOpenLocationChange]);

  const searchAddress = (address: string, additionalCountriesCode?: string) => {
    getLocation({
      variables: {
        address,
        countySearchEnabled: true,
        ...(additionalCountriesCode !== undefined && {
          additionalCountriesCode,
        }),
      },
    });
  };

  const hasStoredLocation = () =>
    !!getGeoLocationFromStorage()?.name &&
    !!getGeoLocationFromStorage()?.stateCode?.trim() &&
    !isNonMember(callingUser);

  // Returns true when a lookup was triggered from the member address or portal zip code
  const searchMemberOrPortalLocation = () => {
    if (isOpenLocationChange) {
      return false;
    }
    if (memberLocation) {
      searchAddress(encodeURIComponent(memberLocation));
      return true;
    }
    const portalZipCode = portalData?.portalEligibilityData?.zipCode;
    if (portalZipCode) {
      searchAddress(portalZipCode);
      return true;
    }
    return false;
  };

  // Returns true when a lookup was triggered from the guest zip code
  const searchGuestLocation = () => {
    if (!guestData?.zipCode) {
      return false;
    }
    searchAddress(guestData.zipCode);
    return true;
  };

  const deviceLocationFirstForFirstTimeLogin = () => {
    if (hasStoredLocation()) {
      setCurrentLocationStatus(locationStates.HAS_LOCATION);
      return;
    }

    const portalZipCode = portalData?.portalEligibilityData?.zipCode;
    const memberZipCode =
      filterdMemberLocation?.length > 0
        ? filterdMemberLocation[filterdMemberLocation.length - 1]
        : '';
    const portalOrMemberLocation = portalZipCode || memberZipCode;

    if (
      memberLocation &&
      hasIncompleteAddress(splitedMemberLocation) &&
      !isOpenLocationChange &&
      portalOrMemberLocation
    ) {
      searchAddress(portalOrMemberLocation, countryCodes ?? '');
      return;
    }

    if (searchMemberOrPortalLocation()) {
      return;
    }

    if (!memberLocation && !searchGuestLocation()) {
      setIsDevLocLoading(true);
      getDeviceLocation();
    }
  };

  const memberLocationFirstForFirstTimeLogin = () => {
    if (hasStoredLocation()) {
      setCurrentLocationStatus(locationStates.HAS_LOCATION);
      return;
    }

    if (searchMemberOrPortalLocation()) {
      return;
    }

    if (!searchGuestLocation()) {
      setCurrentLocationStatus(locationStates.LOCATION_NOT_FOUND);
    }
  };

  useEffect(() => {
    if (!isDevLocLoading && currentLocationStatus === locationStates.LOADING) {
      enableMemberDefaultLocation
        ? deviceLocationFirstForFirstTimeLogin()
        : memberLocationFirstForFirstTimeLogin();
    }
  }, [isDevLocLoading, currentLocationStatus]);

  const getMemberCurrentLocation = useCallback(() => {
    if (isNonMember(callingUser) && !currentLocationStatus) {
      setCurrentLocationStatus(locationStates.LOADING);
    } else if (!currentLocationStatus) {
      setCurrentLocationStatus(locationStates.LOADING);
      if (!enableMemberDefaultLocation) {
        setIsDevLocLoading(true);
        getDeviceLocation();
      }
    }
  }, [currentLocationStatus, portalData?.portalEligibilityData?.callingUser]);

  useEffect(() => {
    getMemberCurrentLocation();
  }, [currentLocationStatus, portalData?.portalEligibilityData?.callingUser]);

  if (!enableSearchLocation) {
    return <React.Fragment>{children}</React.Fragment>;
  }

  return (
    <React.Fragment>
      {(() => {
        switch (currentLocationStatus) {
          case locationStates.LOADING:
            return (
              <div style={LOADER_CENTER_STYLES}>
                <LoadingSpinner ariaLoadingLabel="loading results" isLoading />
              </div>
            );
          case locationStates.HAS_LOCATION:
            return children;
          case locationStates.LOCATION_NOT_FOUND:
            return (
              <Flex
                alignContent="stretch"
                alignItems="center"
                data-auto-testid="search-location-view"
                data-testid="search-location-view"
                direction="column"
              >
                <Layout.Stack alignLayout="center">
                  <IconBrand
                    css={{
                      marginTop: '52px',
                    }}
                    data-auto-testid="location-icon"
                    data-testid="location-icon"
                    icon="location_pin"
                    size="84px"
                    variant="twotone"
                  />
                </Layout.Stack>
                <Layout.Stack
                  alignItems="center"
                  css={{
                    display: 'flex',
                    width: '472px',
                    textAlign: 'center',
                    padding: '16px',
                  }}
                >
                  <Heading
                    css={{
                      '@media (max-width: 744px)': {
                        '&.abyss-heading-root': {
                          alignSelf: 'stretch',
                          fontSize: '36.49px !important',
                          lineHeight: '44px !important',
                          fontWeight: '600',
                        },
                      },
                    }}
                    data-auto-testid="heading-location"
                    data-testid="heading-location"
                    level={1}
                    size="xl"
                  >
                    {t(heading)}
                  </Heading>
                  <Text
                    css={{
                      color: '$neutralGray7 !important',
                      '@media (max-width: 744px)': {
                        '&.abyss-heading-root': {
                          fontSize: '18px !important',
                          fontWeight: '500',
                          lineHeight: '24px !important',
                        },
                      },
                    }}
                    data-auto-testid="location-Title-info-text"
                    data-testid="location-Title-info-text"
                  >
                    {t('LocationTitleInfoText')}
                  </Text>
                </Layout.Stack>
                <Layout.Stack
                  className="full-width-layout-stack"
                  css={{
                    width: '375px',
                    padding: '12px 16px 16px 16px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    display: 'flex',
                    gap: '16px',
                    '&.full-width-layout-stack': {
                      alignItems: 'flex-start',
                      'div:nth-of-type(2)': {
                        width: '100%',
                      },
                    },
                  }}
                >
                  <Text
                    css={{
                      color: '$core.color.brand.100',
                      fontWeight: '600 !important',
                      '@media (max-width: 744px)': {
                        '&.abyss-heading-root': {
                          fontSize: '18px !important',
                          lineHeight: '24px !important',
                          textAlign: 'left !important',
                        },
                      },
                    }}
                    data-auto-testid="location-input-info-text"
                    data-testid="location-input-info-text"
                  >
                    {t('LocationInputInfoText')}
                  </Text>
                  <LocationInput
                    findUserLocation={getDeviceLocation}
                    setCustomLocation={setCustomLocation}
                  />
                </Layout.Stack>
                <Divider height="1px" />
                <Layout.Stack
                  css={{
                    width: '375px',
                    padding: '12px 16px 16px 16px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '16px',
                    'div:nth-of-type(1)': {
                      width: '100%',
                    },
                  }}
                >
                  <Button
                    css={{
                      width: '100%',
                    }}
                    data-auto-testid="submit-button"
                    data-testid="submit-button"
                    isDisabled={!customLocation?.place_name}
                    onClick={() => {
                      if (
                        includes(
                          toUpper(customLocation?.id),
                          toUpper(Constants.DISTRICT)
                        )
                      )
                        setIsCountySearch(true);
                      const location: CustomLocation = {
                        id: customLocation?.id,
                        name: customLocation?.place_name,
                        longitude: customLocation?.center
                          ? customLocation.center[0]
                          : customLocation?.longitude,
                        latitude: customLocation?.center
                          ? customLocation.center[1]
                          : customLocation?.latitude,
                        stateCode: customLocation?.stateCode,
                      };
                      setGeoLocationToStorage(location);
                      setCurrentLocationStatus(locationStates.HAS_LOCATION);

                      if (handleOnClose) {
                        handleOnClose();
                      }
                    }}
                    size="$md"
                    type="submit"
                  >
                    {t('Apply')}
                  </Button>
                </Layout.Stack>
              </Flex>
            );
          default:
            return null;
        }
      })()}
    </React.Fragment>
  );
};
